/**
 * Showcase vs live display values for Payment Terms.
 *
 * Showcase (`B.PAYMENT_TERMS_SHOWCASE`) uses the hardcoded PT_* constants.
 * Live databag views never copy those constants. Absent backend values stay
 * null and render as "Missing" (or "").
 */
import {PT_KPIS, PT_LONG_TAIL, PT_PERIOD, PT_POLICY} from "./paymentTermsDemoData.ts";
import type {PtSummary, PtSupplier, PtTermMapping, PtTrendPoint} from "./PaymentTermsClasses.ts";
import {formatCompactCurrency, formatPct} from "./paymentTermsUtils.ts";

export const MISSING = 'Missing';

export type PtPageDisplay = {
    /** True only when the showcase flag is on. Live never mixes in PT_* totals. */
    isShowcase: boolean;
    currency: string | null;
    totalPositiveSpend: number | null;
    weightedAvgTerm: number | null;
    termCoveragePct: number | null;
    immediateExposure: number | null;
    immediateExposurePct: number | null;
    spendBelowTarget: number | null;
    spendBelowTargetPct: number | null;
    wcOpportunity: number | null;
    quickWinOpportunity: number | null;
    suppliersMeetingPolicyPct: number | null;
    supplierCount: number | null;
    transactionCount: number | null;
    suppliersWithMultipleTerms: number | null;
    multiTermSpend: number | null;
    multiTermQuickWin: number | null;
    suppliersMeetingPolicy: number | null;
    distinctTermVariants: number | null;
    nonStandardTermSpend: number | null;
    targetTermDays: number | null;
    approvedTerms: number[] | null;
    scopeNote: string | null;
    periodFrom: string | null;
    periodTo: string | null;
    periodLabel: string | null;
    longTailCount: number | null;
    longTailWcOpportunity: number | null;
};

function asNum(value: number | string | null | undefined): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function ratio(numerator: number | null, denominator: number | null): number | null {
    if (numerator == null || denominator == null || denominator === 0) return null;
    return numerator / denominator;
}

function weightedAvgTermFromSuppliers(suppliers: PtSupplier[]): number | null {
    const spend = suppliers.reduce((sum, row) => sum + row.spend, 0);
    if (!spend) return null;
    return suppliers.reduce((sum, row) => sum + row.currentTerm * row.spend, 0) / spend;
}

/** SHOWCASE-ONLY: copy of paymentTermsDemoData.ts. Do not call for a real databag. */
export function showcaseDisplay(): PtPageDisplay {
    return {
        isShowcase: true,
        currency: PT_KPIS.currency,
        totalPositiveSpend: PT_KPIS.totalPositiveSpend,
        weightedAvgTerm: PT_KPIS.weightedAvgTerm,
        termCoveragePct: PT_KPIS.termCoveragePct,
        immediateExposure: PT_KPIS.immediateExposure,
        immediateExposurePct: PT_KPIS.immediateExposurePct,
        spendBelowTarget: PT_KPIS.spendBelowTarget,
        spendBelowTargetPct: PT_KPIS.spendBelowTargetPct,
        wcOpportunity: PT_KPIS.wcOpportunity,
        quickWinOpportunity: PT_KPIS.quickWinOpportunity,
        suppliersMeetingPolicyPct: PT_KPIS.suppliersMeetingPolicyPct,
        supplierCount: PT_KPIS.supplierCount,
        transactionCount: PT_KPIS.transactionCount,
        suppliersWithMultipleTerms: PT_KPIS.suppliersWithMultipleTerms,
        multiTermSpend: PT_KPIS.multiTermSpend,
        multiTermQuickWin: PT_KPIS.multiTermQuickWin,
        suppliersMeetingPolicy: PT_KPIS.suppliersMeetingPolicy,
        distinctTermVariants: PT_KPIS.distinctTermVariants,
        nonStandardTermSpend: PT_KPIS.nonStandardTermSpend,
        targetTermDays: PT_POLICY.targetTermDays,
        approvedTerms: [...PT_POLICY.approvedTerms],
        scopeNote: PT_POLICY.scopeNote,
        periodFrom: PT_PERIOD.from,
        periodTo: PT_PERIOD.to,
        periodLabel: PT_PERIOD.label,
        longTailCount: PT_LONG_TAIL.count,
        longTailWcOpportunity: PT_LONG_TAIL.wcOpportunity,
    };
}

export function liveDisplay(args: {
    summary?: PtSummary;
    suppliers?: PtSupplier[];
    mapping?: PtTermMapping[];
    trend?: PtTrendPoint[];
}): PtPageDisplay {
    const {summary, suppliers, mapping, trend} = args;
    const above = asNum(summary?.buckets.spendAtOrAboveTarget);
    const belowBucket = asNum(summary?.buckets.spendBelowTarget);
    const bucketTotal = above != null && belowBucket != null ? above + belowBucket : null;
    const totalPositiveSpend = bucketTotal && bucketTotal > 0
        ? bucketTotal
        : asNum(summary?.kpis.totalSpend);
    const spendBelowTarget = belowBucket ?? asNum(summary?.kpis.belowTargetSpend);
    const immediateExposure = asNum(summary?.kpis.immediateSpend);
    const supplierCount = summary ? summary.kpis.supplierCount : null;
    const meeting = summary ? summary.policy.suppliersMeetingPolicy : null;
    const multiCount = summary ? summary.policy.suppliersWithMultipleTerms : null;
    const multiRows = suppliers?.filter(row => row.multipleTerms);
    const approvedDays = mapping
        ? [...new Set(mapping.filter(row => row.approved).map(row => row.days))].sort((a, b) => a - b)
        : null;
    const first = summary?.trend.firstMonth ?? trend?.[0]?.month ?? null;
    const last = summary?.trend.latestMonth ?? trend?.[trend.length - 1]?.month ?? null;

    return {
        isShowcase: false,
        currency: null,
        totalPositiveSpend,
        weightedAvgTerm: suppliers ? weightedAvgTermFromSuppliers(suppliers) : null,
        termCoveragePct: null,
        immediateExposure,
        immediateExposurePct: ratio(immediateExposure, totalPositiveSpend),
        spendBelowTarget,
        spendBelowTargetPct: ratio(spendBelowTarget, totalPositiveSpend),
        wcOpportunity: asNum(summary?.kpis.wcOpportunity),
        quickWinOpportunity: asNum(summary?.kpis.quickWinOpportunity),
        suppliersMeetingPolicyPct: ratio(meeting, supplierCount),
        supplierCount,
        transactionCount: null,
        suppliersWithMultipleTerms: multiCount,
        multiTermSpend: multiRows ? multiRows.reduce((sum, row) => sum + row.spend, 0) : null,
        multiTermQuickWin: multiRows
            ? multiRows.reduce((sum, row) => sum + row.quickWinOpportunity, 0)
            : null,
        suppliersMeetingPolicy: meeting,
        distinctTermVariants: summary ? summary.mapping.termCount : (mapping ? mapping.length : null),
        nonStandardTermSpend: mapping
            ? mapping.filter(row => !row.approved).reduce((sum, row) => sum + row.estSpend, 0)
            : null,
        targetTermDays: asNum(summary?.policy.targetTermDays),
        approvedTerms: approvedDays,
        scopeNote: null,
        periodFrom: first,
        periodTo: last,
        periodLabel: null,
        longTailCount: supplierCount != null && suppliers != null
            ? Math.max(0, supplierCount - suppliers.length)
            : null,
        longTailWcOpportunity: null,
    };
}

export function textOrMissing(value: string | null | undefined): string {
    return value ? value : MISSING;
}

export function intOrMissing(value: number | null | undefined): string {
    return value == null ? MISSING : value.toLocaleString('en-US');
}

export function daysLabel(value: number | null | undefined, suffix = ' days'): string {
    return value == null ? MISSING : `${value}${suffix}`;
}

export function pctOrMissing(value: number | null | undefined): string {
    return value == null ? MISSING : formatPct(value);
}

export function moneyOrMissing(value: number | null | undefined, currency: string | null): string {
    if (value == null) return MISSING;
    if (!currency) {
        return new Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: value >= 1_000_000 ? 2 : 0,
        }).format(value);
    }
    return formatCompactCurrency(value, currency);
}

export function periodRange(d: PtPageDisplay): string {
    if (d.periodFrom && d.periodTo) return `${d.periodFrom} – ${d.periodTo}`;
    return MISSING;
}

export function targetDaysPhrase(days: number | null | undefined): string {
    return days == null ? MISSING : `${days}-day`;
}

export function formatTableMoney(value: number, currency: string | null): string {
    if (!currency) {
        return new Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: value >= 1_000_000 ? 2 : 0,
        }).format(value);
    }
    return formatCompactCurrency(value, currency);
}

export function formatFullMoney(value: number, currency: string | null): string {
    if (!currency) {
        return new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(value);
    }
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}
